import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Wrapper } from '../style'
import { Table, Modal, Input, Select, message } from 'antd';
import { MakeApiCall } from "../../../../../apis";
import { useDispatch } from 'react-redux';
import { setData as setDataAction } from '../../../../../redux/actions';
import { useCanEdit } from '../../../../../contexts/SubUserAccessContext';

const { Option } = Select;

function SpApi({ loading, list, userData, fetchList }) {
    const canEdit = useCanEdit();
    const applicationId = 'amzn1.sp.solution.80009372-b6f7-4d5c-be85-c89653907b26';
    const version = 'beta';
    const redirect_uri = 'https://app.sellerview.ai/settings/marketplace-credentials?tab=spApi';
    const hasCalledApi = useRef(false);
    const dispatch = useDispatch();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [sellerName, setSellerName] = useState(localStorage.getItem("spapi_sellerName") || "");
    const [selectedRegion, setSelectedRegion] = useState(localStorage.getItem("spapi_region") || "");
    const [selectedMarketplace, setSelectedMarketplace] = useState(localStorage.getItem("spapi_marketplace") || "");

    const allMarketplaces = [
        // North America
        { id: "ATVPDKIKX0DER", name: "Amazon.com", sales_channel: "US", region_name: "NA" },
        { id: "A2EUQ1WTGCTBG2", name: "Amazon.ca", sales_channel: "Canada", region_name: "NA" },
        { id: "A1AM78C64UM0Y8", name: "Amazon.com.mx", sales_channel: "Mexico", region_name: "NA" },
        { id: "A2Q3Y263D00KWC", name: "Amazon.com.br", sales_channel: "Brazil", region_name: "NA" },

        // Europe
        { id: "A1PA6795UKMFR9", name: "Amazon.de", sales_channel: "Germany", region_name: "EU" },
        { id: "A1RKKUPIHCS9HS", name: "Amazon.es", sales_channel: "Spain", region_name: "EU" },
        { id: "A13V1IB3VIYZZH", name: "Amazon.fr", sales_channel: "France", region_name: "EU" },
        { id: "A1F83G8C2ARO7P", name: "Amazon.co.uk", sales_channel: "UK", region_name: "EU" },
        { id: "APJ6JRA9NG5V4", name: "Amazon.it", sales_channel: "Italy", region_name: "EU" },
        { id: "A1805IZSGTT6HS", name: "Amazon.nl", sales_channel: "Netherlands", region_name: "EU" },
        { id: "A2NODRKZP88ZB9", name: "Amazon.se", sales_channel: "Sweden", region_name: "EU" },
        { id: "A1C3SOZRARQ6R3", name: "Amazon.pl", sales_channel: "Poland", region_name: "EU" },
        { id: "A17E79C6D8DWNP", name: "Amazon.be", sales_channel: "Belgium", region_name: "EU" },

        // Asia-Pacific
        { id: "A21TJRUUN4KGV", name: "Amazon.in", sales_channel: "India", region_name: "FE" },
        { id: "A19VAU5U5O7RUS", name: "Amazon.sg", sales_channel: "Singapore", region_name: "FE" },
        { id: "A39IBJ37TRP1C6", name: "Amazon.au", sales_channel: "Australia", region_name: "FE" },
        { id: "A1VC38T7YXB528", name: "Amazon.co.jp", sales_channel: "Japan", region_name: "FE" },

        // Middle East
        { id: "A2VIGQ35RCS4UG", name: "Amazon.ae", sales_channel: "UAE", region_name: "FE" },
        { id: "A33AVAJ2PDY3EV", name: "Amazon.sa", sales_channel: "Saudi Arabia", region_name: "FE" },
        // { id: "A17E79C6D8DWNP", name: "Amazon.eg", sales_channel: "Egypt", region_name: "FE" },
    ];

    // --- Actions ---

    const handleDeleteCredentials = (id) => {
        if (!canEdit) return;
        Modal.confirm({
            className: 'marketplace-delete-modal',
            title: <span className="marketplace-modal-title">Delete Credentials</span>,
            content: <span className="marketplace-modal-body">Are you sure you want to delete these credentials? This action cannot be undone.</span>,
            okText: <span className="marketplace-modal-btn-label">Yes, Delete</span>,
            okType: 'danger',
            cancelText: <span className="marketplace-modal-btn-label">No</span>,
            onOk: async () => {
                try {
                    const response = await MakeApiCall(`user-credentials/${id}`, 'delete', null, true, {}, userData?.token);
                    if (response?.status) {
                        message.success("Credentials deleted successfully");
                        // Refresh credentials list in UI
                        if (fetchList) await fetchList(); // Refresh data via parent function
                        else window.location.reload(); // Fallback refresh
                        // Update top-level user flag for SP API connection
                        try {
                            await refreshAndUpdateSpFlag();
                        } catch (e) {
                            console.error('Error refreshing SP flag after delete:', e);
                        }
                    } else {
                        message.error(response?.message || "Failed to delete credentials");
                    }
                } catch (error) {
                    message.error("Something went wrong while deleting");
                }
            },
        });
    };

    // Refresh the credentials and update the top-level user flag is_amz_sp_api_connected
    const refreshAndUpdateSpFlag = useCallback(async () => {
        try {
            // Prefer using passed-in fetchList to avoid duplicate API calls and reuse the parent cache.
            let records = null;
            if (typeof fetchList === 'function') {
                try {
                    records = await fetchList();
                } catch (e) {
                    // ignore and fallback to direct API
                    records = null;
                  console.warn('fetchList threw, falling back to API call in refreshAndUpdateSpFlag', e);
                }
            }

            if (!records) {
                const resp = await MakeApiCall('user-credentials', 'get', null, true, {}, userData?.token);
                if (resp?.status) records = resp?.data?.records || {};
            }

            const spList = (records && records['SP-API']) || [];
            const hasSp = Array.isArray(spList) && spList.length > 0;

            if (userData) {
                const currentFlag = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
                if (currentFlag !== hasSp) {
                    const newUserData = {
                        ...userData,
                        user: {
                            ...userData.user,
                            user_data: {
                                ...userData.user.user_data,
                                is_amz_sp_api_connected: hasSp ? 1 : 0,
                            },
                        },
                    };
                    // Dispatch to Redux store
                    try {
                        dispatch(setDataAction(newUserData));
                    } catch (err) {
                        console.error('Error dispatching setDataAction in refreshAndUpdateSpFlag:', err);
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching credentials for refreshAndUpdateSpFlag:', err);
        }
    }, [userData, dispatch, fetchList]);

    const handleMarketplaceChange = (marketplaceId) => {
        setSelectedMarketplace(marketplaceId);
        const marketplace = allMarketplaces.find(m => m.id === marketplaceId);
        if (marketplace) {
            setSelectedRegion(marketplace.region_name);
        }
    };

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // Inside AmazonAdvertising.jsx

    useEffect(() => {
        const url = new URL(window.location.href);
        const shouldOpenCredsModal = url.searchParams.get('creds') === 'true';
        const tab = url.searchParams.get('tab') || 'spApi';

        if (!shouldOpenCredsModal || tab !== 'spApi') return;

        setIsModalOpen(true);

        // Remove the one-time trigger so refresh/back does not reopen the modal.
        url.searchParams.delete('creds');
        if (window.history.replaceState) {
            window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
        }
    }, []); // Runs once on mount



    // Sequential API flow: Fetch app_id, then redirect with application_id, and store app_id for callback
    const handleAmazonLogin = async () => {
        localStorage.setItem("spapi_sellerName", sellerName);
        localStorage.setItem("spapi_region", selectedRegion);
        localStorage.setItem("spapi_marketplace", selectedMarketplace);

        try {
            // Step 1: Fetch Application Data
            const response = await MakeApiCall(
                'sp-credentials/available-application',
                'get',
                null,
                true,
                {},
                userData?.token
            );
            if (response && response.code === 200 && response.data && response.data.app_id) {
                const app_id = response.data.app_id;
                const application_id = response.data.application_id || applicationId;
                // Store app_id for use in callback POST
                localStorage.setItem("spapi_app_id", app_id);

                // Step 2: Redirect to Amazon consent page with application_id
                const state = `${userData?.user?.user_data?.id}!!${selectedRegion}!!${selectedMarketplace}`;
                const baseUrl = 'https://sellercentral.amazon.in/apps/authorize/consent';
                const params = new URLSearchParams({
                    application_id,
                    version,
                    state,
                    redirect_uri
                });
                window.location.href = `${baseUrl}?${params.toString()}`;
            } else {
                message.error(response?.message || "Failed to fetch application data");
            }
        } catch (err) {
            message.error("Error fetching application data");
        }
    };

    useEffect(() => {
        const handleCallback = async () => {
            const url = new URL(window.location.href);
            const spapi_oauth_code = url.searchParams.get('spapi_oauth_code');
            const amazon_state = url.searchParams.get('state');
            const selling_partner_id = url.searchParams.get('selling_partner_id');

            if (!spapi_oauth_code || !selling_partner_id || !amazon_state) return;

            if (!hasCalledApi.current) {
                hasCalledApi.current = true;
                const parts = amazon_state.split('!!');
                const marketplace_id = parts[2] || null;
                const savedSellerName = localStorage.getItem("spapi_sellerName") || "Unknown Seller";
                const app_id = localStorage.getItem("spapi_app_id");

                try {
                    // Step 3: Submit Callback URL with app_id
                    await MakeApiCall(
                        `sp-credentials/callback-url`,
                        'post',
                        {
                            spapi_oauth_code,
                            selling_partner_id,
                            marketplace_id,
                            seller_name: savedSellerName,
                            ...(app_id ? { app_id } : {})
                        },
                        true,
                        {},
                        userData?.token
                    );

                    if (window.history.replaceState) {
                        const cleanUrl = window.location.origin + window.location.pathname;
                        window.history.replaceState({}, document.title, cleanUrl);
                    }

                    localStorage.removeItem("spapi_sellerName");
                    localStorage.removeItem("spapi_region");
                    localStorage.removeItem("spapi_marketplace");
                    localStorage.removeItem("spapi_app_id");

                    // Refresh credentials UI and update top-level user flag
                    try {
                        if (fetchList) await fetchList();
                        await refreshAndUpdateSpFlag();
                    } catch (e) {
                        console.error('Error refreshing after SP callback:', e);
                    }

                    // Keep original behavior of reloading to ensure any other state is refreshed
                    window.location.reload();
                } catch (err) {
                    console.error("Error in API call:", err);
                }
            }
        };
        handleCallback();
    }, [userData?.token, fetchList, refreshAndUpdateSpFlag]);

    const columns = [
        // { title: 'Credential Type', dataIndex: 'credential_type', key: 'credential_type' },
        { title: 'Seller Name', dataIndex: 'seller_name', key: 'seller_name', render: value => <div className='fw-bold'>{value}</div> },
        // { title: 'Region', dataIndex: 'region', key: 'region' },
        { title: 'Marketplace', dataIndex: 'marketplace', key: 'marketplace' },
        // { title: 'Marketplace ID', dataIndex: 'marketplace_id', key: 'marketplace_id' },
        {
            title: 'Expires On',
            dataIndex: 'credential_renewal_due_at',
            key: 'credential_renewal_due_at',
            render: (value) => {
                if (!value) return '-';
                // value assumed to be a unix timestamp (seconds)
                const date = new Date(value * 1000); 
                const now = new Date();
                const diffMs = date.getTime() - now.getTime();
                const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
                let daysRemain = '';
                if (diffDays > 1) {
                    daysRemain = ` (${diffDays} days Remaining)`;
                } else if (diffDays === 1) {
                    daysRemain = ` (1 day Remaining)`;
                } else if (diffDays === 0) {
                    daysRemain = ` (expires today)`;
                } else {
                    daysRemain = ` (expired)`;
                }
                return (
                    <div>
                        <div>{date.toLocaleDateString()} {date.toLocaleTimeString()}</div>
                        <div className="marketplace-expiry-subtext">{daysRemain}</div>
                    </div>
                );
           
            }
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            align: 'center',
            render: (_, record) => (
                <span
                    className="cursor-pointer text-hover-danger"
                    style={{ opacity: canEdit ? 1 : 0.45, pointerEvents: canEdit ? 'auto' : 'none' }}
                    onClick={() => handleDeleteCredentials(record.id)}
                >
                    <i className="bi bi-trash fs-3 text-danger"></i>
                </span>
            )
        }
    ];

    return (
        <Wrapper>
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    <div className="card card-xxl-stretch mb-5 mb-xl-8">
                        <div className="card-header border-bottom-dashed">
                            <h3 className="card-title fw-bolder">Seller Central Credentials List</h3>
                            <div className="card-toolbar">
                                {/* {(!list || list.length === 0) && (
                                    <button className="btn btn-light-google" onClick={openModal}>
                                        Login With Amazon
                                    </button>
                                )} */}
                            </div>
                        </div>
                        <div className="card-body py-3 px-3">
                            {isMobile ? (
                                <div>
                                    {loading ? (
                                        <div className="text-muted py-5 text-center">Loading...</div>
                                    ) : !list || list.length === 0 ? (
                                        <div className="text-muted py-5 text-center">No credentials found.</div>
                                    ) : (
                                        list.map((item) => (
                                            <div key={item.id} className="card border mb-4 marketplace-mobile-credential-card">
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div className="fw-bolder fs-6">{item.seller_name || "-"}</div>
                                                        <span
                                                            className="cursor-pointer text-hover-danger"
                                                            style={{ opacity: canEdit ? 1 : 0.45, pointerEvents: canEdit ? 'auto' : 'none' }}
                                                            onClick={() => handleDeleteCredentials(item.id)}
                                                        >
                                                            <i className="bi bi-trash fs-3 text-danger"></i>
                                                        </span>
                                                    </div>
                                                    <div className="text-muted fs-7 mb-1">
                                                        <span className="text-muted"><b>Credential Type:</b></span> <span className="marketplace-mobile-value">{item.credential_type || "-"}</span>
                                                    </div>
                                                    <div className="text-muted fs-7 mb-1">
                                                        <span className="text-muted"><b>Region:</b></span> <span className="marketplace-mobile-value">{item.region || "-"}</span>
                                                    </div>
                                                    <div className="text-muted fs-7 mb-1">
                                                        <span className="text-muted"><b>Marketplace:</b></span> <span className="marketplace-mobile-value">{item.marketplace || "-"}</span>
                                                    </div>
                                                    <div className="text-muted fs-7">
                                                        <span className="text-muted"><b>Marketplace ID:</b></span> <span className="marketplace-mobile-value">{item.marketplace_id || "-"}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table
                                        scroll={{ x: 'max-content' }}
                                        columns={columns}
                                        loading={loading}
                                        dataSource={list}
                                        rowKey="id"
                                        pagination={false}
                                        locale={{
                                            emptyText: (
                                              <div className="marketplace-empty-state d-flex flex-column align-items-center justify-content-center text-center px-4 py-5">
                                                {/* Icon Section */}
                                                <div className="marketplace-empty-icon-wrap d-flex align-items-center justify-content-center mb-4 position-relative">
                                                  <div className="marketplace-empty-icon-ring" />
                                          
                                                  <i className="fab fa-amazon marketplace-empty-icon" />
                                                  {/* <img src="https://s3.amazonaws.com/cdn.hotglue.xyz/images/logos/amazon-ads.png" style={{width: "50px", filter: "brightness(1) grayscale(1)"}} /> */}
                                                </div>
                                          
                                                {/* Content */}
                                                <h3 className="fw-bold mb-2 marketplace-empty-title">
                                                  Seller Central Credentials Not Found
                                                </h3>
                                          
                                                <p className="mb-4 marketplace-empty-desc">
                                                  Connect your Amazon SP-API account to manage listings, inventory, orders, and automate your selling operations from one dashboard.
                                                    </p>
                                          
                                                {/* CTA Button */}
                                                <button
                                                  onClick={openModal}
                                                  disabled={!canEdit}
                                                  className="btn btn-danger border-0 position-relative overflow-hidden marketplace-connect-btn"
                                                >
                                                  Connect Amazon Account
                                                </button>
                                          
                                                {/* Footer */}
                                                {/* <div
                                                  className="d-flex align-items-center gap-2 mt-4"
                                                  style={{
                                                    color: "#9CA3AF",
                                                    fontSize: "13px",
                                                  }}
                                                >
                                                  <i className="fas fa-shield-alt"></i>
                                                  Secure OAuth Authentication
                                                </div> */}
                                          
                                              </div>
                                            ),
                                          }}
                                    />
                              
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                title={<span className="marketplace-modal-title">Amazon Login Details</span>}
                open={isModalOpen}
                onCancel={closeModal}
                onOk={handleAmazonLogin}
                okText="Continue"
                okButtonProps={{
                    disabled: !sellerName.trim() || !selectedMarketplace,
                }}
                footer={[
                    <button key="back" type="button" className="btn btn-light me-3 marketplace-modal-secondary-btn" onClick={closeModal}>Close</button>,
                    <button key="submit" type="button" className="btn btn-danger" onClick={handleAmazonLogin} >
                     Continue
                    </button>
                  ]}
                  className="marketplace-login-modal"
                  centered
            >
                <div className="mb-4">
                    <label className="fw-bold mb-2">Seller Name</label>
                    <Input
                        value={sellerName}
                        onChange={(e) => setSellerName(e.target.value)}
                        placeholder="e.g. My Brand Store"
                        className="py-2"
                    />
                </div>

                <div className="mb-4">
                    <label className="fw-bold mb-2">Select Marketplace</label>
                    <Select
                        showSearch
                        style={{ width: "100%" }}
                        value={selectedMarketplace || undefined}
                        onChange={handleMarketplaceChange}
                        placeholder="Search or Select Marketplace"
                        optionFilterProp="label"
                        className="h-40px"

                    >
                        {allMarketplaces.map(m => (
                            <Option key={m.id} value={m.id} label={`${m.name} ${m.sales_channel}`}>
                                <div className="d-flex align-items-center">
                                    <img
                                        src={`/assets/media/domainImage/${m.id}.png`}
                                        style={{ height: "18px", width: "24px", objectFit: "contain" }}
                                        loading="lazy"
                                        className="me-3"
                                        onError={(e) => {
                                            e.target.src = "/assets/media/domainImage/red-flag.png";
                                        }}
                                        alt={m.name}
                                    />
                                    <span>
                                        {m.name} <span className="text-muted fs-8">({m.sales_channel})</span>
                                    </span>
                                </div>
                            </Option>
                        ))}
                    </Select>
                </div>
            </Modal>
        </Wrapper>
    )
}

export default SpApi;

